import { useMemo, useCallback } from 'react';
import type {
  Column,
  Product as PriceBookProduct,
  ProductUnitPrices,
  Price,
  ProductUnitOption
} from '@/features/lista-precios/models/PriceTypes';
import type { Product as CatalogProduct } from '@/features/catalogo-articulos/models/types';
import { usePriceCalculator } from '@/features/lista-precios/hooks/usePriceCalculator';
import { useProductStore } from '@/features/catalogo-articulos/hooks/useProductStore';
import { roundCurrency } from '@/features/lista-precios/utils/price-helpers/pricing';
import { isMinAllowedColumn } from '@/features/lista-precios/utils/price-helpers/columns';
import { useConfigurationContext } from '@/contexts/ContextoConfiguracion';
import { getUnitDisplayForUI } from '@/shared/units/unitDisplay';
import { listaPreciosService } from '@/services/api/lista-precios.service';
import { getBusinessTodayISODate } from '@/shared/time/businessTime';

export interface PriceColumnOption {
  columnId: string;
  label: string;
  price: number;
  isBase: boolean;
  hasExplicitPrice: boolean;
}

export interface DerivedUnitPriceInput {
  sku: string;
  selectedUnitCode?: string;
  priceListId?: string;
}

export interface UnitPriceResult {
  price: number;
  hasPrice: boolean;
  hasExplicitPrice: boolean;
  usedUnitCode?: string;
  usedColumnId?: string;
}

const normalizeUnitCode = (value?: string): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.toUpperCase();
};

const resolvePriceValue = (price: Price | undefined): number | undefined => {
  if (!price) return undefined;
  if (price.type === 'fixed') {
    return price.value > 0 ? price.value : undefined;
  }
  if (!price.ranges || price.ranges.length === 0) {
    return undefined;
  }
  const sortedRanges = [...price.ranges].sort((a, b) => a.minQuantity - b.minQuantity);
  const firstPrice = sortedRanges[0]?.price;
  return typeof firstPrice === 'number' && firstPrice > 0 ? firstPrice : undefined;
};

const salesColumnPredicate = (column: Column): boolean => {
  if (!column.visible) return false;
  return column.kind === 'base' || column.kind === 'manual';
};

const isPositiveNumber = (value?: number): value is number => {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
};

const getUnitFactorFromCatalog = (product: CatalogProduct | undefined, targetUnit?: string): number | undefined => {
  if (!product || !targetUnit) return undefined;
  const normalizedTarget = normalizeUnitCode(targetUnit);
  if (!normalizedTarget) return undefined;
  const baseCode = normalizeUnitCode(product.unidad);
  if (normalizedTarget === baseCode) {
    return 1;
  }
  const match = product.unidadesMedidaAdicionales?.find(unit => normalizeUnitCode(unit.unidadCodigo) === normalizedTarget);
  const factor = match?.factorConversion;
  if (isPositiveNumber(factor)) {
    return factor;
  }
  return undefined;
};

export const usePriceBook = (mode?: 'pos' | 'comprobante') => {
  const { columns: rawColumns, productos: rawProducts, ensurePricesForSku, registerLocalFixedPrice } = usePriceCalculator();
  const columns: Column[] = Array.isArray(rawColumns) ? rawColumns : [];
  const productos = Array.isArray(rawProducts) ? rawProducts : [];
  const { allProducts, onDemandProducts, ensureProductById } = useProductStore({ lazy: true });
  const catalogProducts = useMemo(
    () => (onDemandProducts.length > 0 ? [...allProducts, ...onDemandProducts] : allProducts),
    [allProducts, onDemandProducts]
  );
  const { state: configState } = useConfigurationContext();

  const coerceUnitCode = useCallback((value?: string) => normalizeUnitCode(value), []);

  const selectableColumns = useMemo<Column[]>(() => {
    return columns
      .filter((column: Column) => salesColumnPredicate(column))
      .sort((a: Column, b: Column) => a.order - b.order);
  }, [columns]);

  const channelColumns = useMemo<Column[]>(() => {
    if (mode === 'pos') return selectableColumns.filter((c: Column) => c.usarEnPuntoVenta !== false);
    if (mode === 'comprobante') return selectableColumns.filter((c: Column) => c.usarEnComprobantes !== false);
    return selectableColumns;
  }, [mode, selectableColumns]);

  const minPriceColumn = useMemo<Column | undefined>(() => {
    const col = columns.find((column: Column) => isMinAllowedColumn(column));
    if (!col) return undefined;
    if (mode === 'pos' && col.usarEnPuntoVenta === false) return undefined;
    if (mode === 'comprobante' && col.usarEnComprobantes === false) return undefined;
    return col;
  }, [mode, columns]);
  const globalDiscountColumn = useMemo<Column | undefined>(() => columns.find((column: Column) => column.kind === 'global-discount'), [columns]);
  const globalIncreaseColumn = useMemo<Column | undefined>(() => columns.find((column: Column) => column.kind === 'global-increase'), [columns]);
  const baseColumnId = useMemo(() => selectableColumns.find((column: Column) => column.isBase)?.id, [selectableColumns]);
  const baseColumnUuid = useMemo(() => selectableColumns.find((column: Column) => column.isBase)?.uuid, [selectableColumns]);

  const defaultColumnId = useMemo(() => {
    if (mode === 'pos') return channelColumns.find((c: Column) => c.esPredeterminadoPos)?.id ?? baseColumnId;
    if (mode === 'comprobante') return channelColumns.find((c: Column) => c.esPredeterminadoComprobante)?.id ?? baseColumnId;
    return baseColumnId;
  }, [mode, channelColumns, baseColumnId]);

  const productPriceMap = useMemo(() => {
    const map = new Map<string, PriceBookProduct>();
    productos.forEach((product: PriceBookProduct) => map.set(product.sku, product));
    return map;
  }, [productos]);

  const catalogProductMap = useMemo(() => {
    const map = new Map<string, CatalogProduct>();
    catalogProducts.forEach(product => {
      if (product?.codigo) {
        map.set(product.codigo, product);
      }
    });
    return map;
  }, [catalogProducts]);

  const resolveUnitLabelForProduct = useCallback(
    (product: CatalogProduct | undefined, unitCode?: string) => {
      const normalized = normalizeUnitCode(unitCode);
      if (!normalized) return '';
      if (!product) {
        return getUnitDisplayForUI({ units: configState.units, code: normalized }) || normalized;
      }

      const baseCode = normalizeUnitCode(product.unidad);
      if (baseCode && baseCode === normalized) {
        return (
          getUnitDisplayForUI({
            units: configState.units,
            code: normalized,
            fallbackSymbol: product.unitSymbol,
            fallbackName: product.unitName,
            fallbackDescription: product.unidadMedidaDescripcion,
          }) || normalized
        );
      }

      const match = product.unidadesMedidaAdicionales?.find(
        unit => normalizeUnitCode(unit.unidadCodigo) === normalized
      );

      return (
        getUnitDisplayForUI({
          units: configState.units,
          code: normalized,
          fallbackSymbol: match?.unidadSymbol,
          fallbackName: match?.unidadName,
        }) || normalized
      );
    },
    [configState.units]
  );

  const getUnitLabelForSku = useCallback(
    (sku: string, unitCode?: string) => resolveUnitLabelForProduct(catalogProductMap.get(sku), unitCode),
    [catalogProductMap, resolveUnitLabelForProduct]
  );

  const getUnitPriceWithFallback = useCallback((input: DerivedUnitPriceInput): UnitPriceResult => {
    const sku = input.sku;
    if (!sku) {
      return {
        price: 0,
        hasPrice: false,
        hasExplicitPrice: false,
      };
    }

    const columnId = input.priceListId || baseColumnId;
    if (!columnId) {
      return {
        price: 0,
        hasPrice: false,
        hasExplicitPrice: false,
      };
    }

    const priceBookProduct = productPriceMap.get(sku);
    if (!priceBookProduct) {
      return {
        price: 0,
        hasPrice: false,
        hasExplicitPrice: false,
        usedColumnId: columnId,
      };
    }

    const columnPrices: ProductUnitPrices | undefined = priceBookProduct.prices[columnId];
    if (!columnPrices) {
      return {
        price: 0,
        hasPrice: false,
        hasExplicitPrice: false,
        usedColumnId: columnId,
      };
    }

    const catalogProduct = catalogProductMap.get(sku);
    const normalizedSelectedUnit = coerceUnitCode(input.selectedUnitCode)
      || coerceUnitCode(catalogProduct?.unidad)
      || coerceUnitCode(priceBookProduct.activeUnitCode)
      || '';

    const explicitPriceValue = resolvePriceValue(columnPrices[normalizedSelectedUnit]);
    if (typeof explicitPriceValue === 'number') {
      return {
        price: roundCurrency(explicitPriceValue),
        hasPrice: true,
        hasExplicitPrice: true,
        usedUnitCode: normalizedSelectedUnit,
        usedColumnId: columnId,
      };
    }

    const baseUnitCode = coerceUnitCode(catalogProduct?.unidad)
      || coerceUnitCode(priceBookProduct.activeUnitCode)
      || '';

    const baseUnitPriceValue = baseUnitCode ? resolvePriceValue(columnPrices[baseUnitCode]) : undefined;
    const factor = getUnitFactorFromCatalog(catalogProduct, normalizedSelectedUnit);

    if (typeof baseUnitPriceValue === 'number' && typeof factor === 'number') {
      return {
        price: roundCurrency(baseUnitPriceValue * factor),
        hasPrice: true,
        hasExplicitPrice: false,
        usedUnitCode: normalizedSelectedUnit,
        usedColumnId: columnId,
      };
    }

    return {
      price: 0,
      hasPrice: false,
      hasExplicitPrice: false,
      usedUnitCode: normalizedSelectedUnit,
      usedColumnId: columnId,
    };
  }, [baseColumnId, catalogProductMap, coerceUnitCode, productPriceMap]);

  const unitOptionsBySku = useMemo(() => {
    const map = new Map<string, ProductUnitOption[]>();

    const registerUnit = (
      registry: Map<string, ProductUnitOption>,
      sku: string,
      code?: string,
      isBase = false
    ) => {
      const normalized = coerceUnitCode(code);
      if (!normalized) return;
      const existing = registry.get(normalized);
      if (existing) {
        if (isBase && !existing.isBase) {
          registry.set(normalized, { ...existing, isBase: true });
        }
        return;
      }
      registry.set(normalized, {
        code: normalized,
        label: getUnitLabelForSku(sku, normalized),
        isBase
      });
    };

    productos.forEach(product => {
      const registry = new Map<string, ProductUnitOption>();
      const catalogProduct = catalogProductMap.get(product.sku);

      registerUnit(registry, product.sku, catalogProduct?.unidad, true);
      catalogProduct?.unidadesMedidaAdicionales?.forEach(unit => registerUnit(registry, product.sku, unit?.unidadCodigo));

      registerUnit(registry, product.sku, product.activeUnitCode, true);
      Object.values(product.prices).forEach(unitPrices => {
        Object.keys(unitPrices || {}).forEach(unitCode => registerUnit(registry, product.sku, unitCode));
      });

      map.set(product.sku, Array.from(registry.values()));
    });

    return map;
  }, [catalogProductMap, coerceUnitCode, getUnitLabelForSku, productos]);

  const getPriceOptionsFor = useCallback((sku: string, unitCode?: string): PriceColumnOption[] => {
    if (!sku) return [];
    const options: PriceColumnOption[] = [];
    channelColumns.forEach((column: Column) => {
      const derived = getUnitPriceWithFallback({ sku, selectedUnitCode: unitCode, priceListId: column.id });
      if (derived.hasPrice) {
        options.push({
          columnId: column.id,
          label: column.name,
          price: derived.price,
          isBase: column.isBase,
          hasExplicitPrice: derived.hasExplicitPrice
        });
      }
    });
    return options;
  }, [getUnitPriceWithFallback, channelColumns]);

  const getUnitOptionsForSku = useCallback((sku: string): ProductUnitOption[] => {
    if (!sku) return [];
    return unitOptionsBySku.get(sku) ?? [];
  }, [unitOptionsBySku]);

  const getPreferredUnitForSku = useCallback((sku: string, requestedUnit?: string): string => {
    const options = unitOptionsBySku.get(sku);
    const candidate = coerceUnitCode(requestedUnit);
    if (!options || options.length === 0) {
      return candidate || '';
    }
    if (candidate) {
      if (options.some(option => option.code === candidate)) {
        return candidate;
      }
      return candidate;
    }
    const baseOption = options.find(option => option.isBase);
    return baseOption?.code || options[0].code;
  }, [coerceUnitCode, unitOptionsBySku]);

  const resolveMinPrice = useCallback((sku: string, unitCode?: string): number | undefined => {
    if (!minPriceColumn) return undefined;
    const result = getUnitPriceWithFallback({ sku, selectedUnitCode: unitCode, priceListId: minPriceColumn.id });
    return result.hasPrice ? result.price : undefined;
  }, [getUnitPriceWithFallback, minPriceColumn]);

  const saveManualBasePrice = useCallback(async (input: {
    sku: string;
    unitCode?: string;
    value: number;
  }): Promise<boolean> => {
    if (!baseColumnId || !baseColumnUuid) return false;

    const sku = input.sku?.trim();
    const unitCode = coerceUnitCode(input.unitCode);
    if (!sku || !unitCode || !Number.isFinite(input.value) || input.value <= 0) return false;

    try {
      const vigenteDesde = getBusinessTodayISODate();
      const vigenteHasta = '2099-12-31';
      const valor = roundCurrency(input.value);

      const res = await listaPreciosService.aprenderPrecioSiNoExiste({
        sku,
        columnaId: baseColumnUuid,
        unidadCodigo: unitCode,
        tipo: 'fixed',
        valor,
        rangos: null,
        vigenteDesde,
        vigenteHasta,
      });

      if (!res.ok || !res.data?.aplicado) return false;

      registerLocalFixedPrice({ sku, columnCode: baseColumnId, unitCode, value: valor, vigenteDesde, vigenteHasta });
      return true;
    } catch {
      return false;
    }
  }, [baseColumnId, baseColumnUuid, coerceUnitCode, registerLocalFixedPrice]);

  return {
    hasSelectableColumns: channelColumns.length > 0,
    baseColumnId,
    defaultColumnId,
    priceColumns: channelColumns,
    globalDiscountColumn,
    globalIncreaseColumn,
    getUnitOptionsForSku,
    getPreferredUnitForSku,
    getUnitLabelForSku,
    getPriceOptionsFor,
    resolveMinPrice,
    getUnitPriceWithFallback,
    ensurePricesForSku,
    ensureProductById,
    saveManualBasePrice,
  };
};
